import { DataInputFormField, InputSingleValue, InputTypes } from "../../types";
import { twMerge } from "tailwind-merge";
import Select from "./Select";
import { getBasicInputClassNames } from "./getBasicInputClassNames";
import DateInput from "./DateInput";
import { getOptionString, getOptionValue } from "../../utils";
import { Fragment } from "react/jsx-runtime";

type InputProps = {
    onChange?: (value: InputSingleValue | null) => void;
    disabled?: boolean;
    className?: string;
    inputClassName?: string;
    isDirty: boolean;
    hideError?: boolean;

    dataInputFormProps: DataInputFormField;
};

export default function Input({
    onChange,
    disabled,
    className,
    inputClassName = "",
    isDirty,
    hideError,

    dataInputFormProps,
}: InputProps) {
    const {
        type,
        id,
        value,
        required,
        error,
        valueToDisplay,
        inputClassName: dataInputClassName,
    } = dataInputFormProps;
    const inputStyle = twMerge(
        inputClassName,
        getBasicInputClassNames({
            error: Boolean(error),
            isDirty,
            disabled: Boolean(disabled),
        })
    );

    return (
        <div
            className={twMerge(
                `h-full flex flex-col gap-px group relative`,
                className,
                dataInputClassName
            )}
        >
            {type === InputTypes.RADIO && dataInputFormProps.options ? (
                <div className="flex gap-2 xl:gap-4 items-center h-full">
                    {dataInputFormProps.options.map((option) => {
                        const optionString = getOptionString(option);
                        const optionValue = getOptionValue(option);
                        return (
                            <div
                                key={optionValue}
                                className="flex items-center xl:gap-1 w-14 xl:w-[65.25px]"
                            >
                                <input
                                    id={optionValue?.toString()}
                                    type="radio"
                                    value={optionValue || undefined}
                                    checked={value === optionValue}
                                    onChange={() => onChange?.(optionValue)}
                                    required={required}
                                    disabled={disabled}
                                    className="mr-1 h-4 w-4 accent-blue-dark"
                                />
                                <label
                                    className="text-black font-semibold w-[30px] xl:w-[33px] text-sm xl:text-base"
                                    htmlFor={optionValue?.toString()}
                                >
                                    {optionString}
                                </label>
                            </div>
                        );
                    })}
                </div>
            ) : type === InputTypes.SELECT && dataInputFormProps.options ? (
                <Select
                    value={dataInputFormProps.value}
                    onChange={(val) => {
                        if (dataInputFormProps.multiple) {
                            const newValues =
                                val as typeof dataInputFormProps.value;
                            const currentValues = Array.isArray(
                                dataInputFormProps.value
                            )
                                ? dataInputFormProps.value
                                : [];
                            const diff = newValues
                                .filter((v) => !currentValues.includes(v))
                                .concat(
                                    currentValues.filter(
                                        (v) => !newValues.includes(v)
                                    )
                                );
                            if (diff.length > 0) {
                                onChange?.(diff[0]);
                            }
                        } else {
                            onChange?.(val as InputSingleValue);
                        }
                    }}
                    options={dataInputFormProps.options}
                    multiple={dataInputFormProps.multiple}
                    disabled={disabled}
                    valueToDisplay={valueToDisplay ?? undefined}
                />
            ) : type === InputTypes.TEXT || type === InputTypes.NUMBER ? (
                <input
                    id={id}
                    type={type}
                    value={value ?? ""}
                    onChange={(e) => {
                        const value = e.target.value;
                        onChange?.(
                            type === InputTypes.NUMBER
                                ? value === ""
                                    ? null
                                    : Number(value)
                                : value
                        );
                    }}
                    onKeyDown={(e) => {
                        if (type === InputTypes.NUMBER) {
                            if (
                                dataInputFormProps.validation?.isInteger &&
                                e.key === "."
                            ) {
                                e.preventDefault();
                            }
                            // Prevent entering more decimal places than allowed
                            const decimalPlaces =
                                dataInputFormProps.validation?.decimalPlaces;
                            const isDigit = /\d/.test(e.key);

                            if (decimalPlaces !== undefined && isDigit) {
                                const currentValue = (
                                    e.target as HTMLInputElement
                                ).value;

                                if (currentValue.includes(".")) {
                                    const parts = currentValue.split(".");
                                    const afterTheDot = parts[1];
                                    if (afterTheDot.length >= decimalPlaces) {
                                        e.preventDefault();
                                    }
                                }
                            }
                        }
                    }}
                    required={required}
                    disabled={disabled}
                    className={inputStyle}
                    autoComplete="off"
                />
            ) : type === InputTypes.DATE ? (
                <DateInput
                    date={value ? new Date(value) : null}
                    onChangeDate={(date) => {
                        onChange?.(
                            date ? date.toISOString().replace(/T.+$/, "") : null
                        );
                    }}
                    disabled={disabled}
                    isDirty={isDirty}
                    hasError={Boolean(error)}
                />
            ) : type === InputTypes.FEET_INCHES ? (
                <div className="flex gap-1 xl:gap-2 items-center h-full">
                    {[
                        {
                            field: "feet",
                            value: dataInputFormProps.feetValue,
                            otherValue: dataInputFormProps.inchesValue,
                            label: "ft",
                        },
                        {
                            field: "inches",
                            value: dataInputFormProps.inchesValue,
                            otherValue: dataInputFormProps.feetValue,
                            label: "in",
                        },
                    ].map(({ field, value, otherValue, label }) => (
                        <Fragment key={field}>
                            <input
                                id={`${id}_${field}`}
                                type="number"
                                step={"1"}
                                value={value || ""}
                                onChange={(e) => {
                                    const currentValue =
                                        e.target.value === ""
                                            ? null
                                            : Number(e.target.value);
                                    const other = otherValue ?? 0;
                                    const [feet, inches] =
                                        field === "feet"
                                            ? [currentValue, other]
                                            : [other, currentValue];
                                    const totalInches =
                                        feet !== null || inches !== null
                                            ? (feet ?? 0) * 12 + (inches ?? 0)
                                            : null;
                                    onChange?.(totalInches);
                                }}
                                onKeyDown={(e) => {
                                    if (field === "feet" && e.key === ".") {
                                        e.preventDefault();
                                    }
                                }}
                                placeholder={"0"}
                                required={required}
                                disabled={disabled}
                                className={twMerge(
                                    inputStyle,
                                    "w-[35px] xl:w-[50px]",
                                    "text-center"
                                )}
                                autoComplete="off"
                            />
                            <span className="text-[13px] xl:text-base font-semibold">
                                {label}
                            </span>
                        </Fragment>
                    ))}
                </div>
            ) : null}
            {error && !hideError ? (
                <span
                    className={twMerge(
                        "text-red-error text-center w-32 xl:w-[200px] h-8 shrink-0 font-semibold text-[9px] xl:text-xs !leading-[1.2] overflow-y-auto"
                    )}
                >
                    {error}
                </span>
            ) : null}
        </div>
    );
}
